import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo size="md" /></div>

        {sent ? (
          <div data-testid="forgot-sent" className="p-8 bg-white rounded-3xl card-tactile text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-leaf/15 text-leaf flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-ink mb-2">Check your email</h1>
            <p className="text-stone mb-6">
              If <strong className="text-ink">{email}</strong> matches an account (either the student's email or the parent's email on file), we've sent a password reset link. It expires in 30 minutes.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-sky-hover font-bold hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-sky-hover font-bold text-sm mb-6 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
            <h1 className="font-heading text-4xl font-bold text-ink mb-2">Forgot password?</h1>
            <p className="text-stone mb-8">
              Enter the student's email <em>or</em> the parent's email on file, and we'll send a reset link.
            </p>
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="block text-sm font-bold text-ink mb-2" htmlFor="email">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                  <input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full h-14 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-base font-medium bg-white"
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="forgot-submit"
                disabled={submitting}
                className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
